import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type Configuration,
} from '@azure/msal-browser';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/* Dev bypass mirrors the API's AUTH_DEV_BYPASS so the whole stack can run
   without a tenant. Vite only exposes VITE_-prefixed vars, and this one is
   read at build time — a production bundle built without it cannot be
   flipped into bypass mode from the browser. */
export const DEV_BYPASS = import.meta.env.VITE_AUTH_DEV_BYPASS === 'true';

const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID ?? '';
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID ?? '';

/** The API's exposed scope, e.g. api://<api-client-id>/access_as_user */
const apiScope = import.meta.env.VITE_API_SCOPE ?? '';

const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    /* sessionStorage keeps the token out of other tabs and clears on close,
       which is the right default for an internal tool on shared machines. */
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

export const msalInstance = DEV_BYPASS ? null : new PublicClientApplication(msalConfig);

const SCOPES = () => (apiScope ? [apiScope] : ['User.Read']);

/** Why the last session ended, shown once on the sign-in screen. */
export const SIGNED_OUT_KEY = 'coo-signed-out-reason';

/**
 * When the person was last active, in localStorage so every tab shares it
 * (components/IdleSignOut.tsx). Cleared on sign-out and stamped on sign-in:
 * a stale stamp left behind by the last session made a fresh sign-in look
 * past the idle limit, so the app signed straight back out — a loop.
 */
export const ACTIVITY_KEY = 'coo-last-activity';
export function stampActivity() {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
  } catch {
    /* storage blocked: the tab's own timer still works */
  }
}
function clearActivity() {
  try {
    localStorage.removeItem(ACTIVITY_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * End the MSP-OPS session after inactivity (components/IdleSignOut.tsx).
 *
 * Clears this app's tokens and account, then reloads to the sign-in screen.
 * Deliberately NOT a full Microsoft sign-out: onRedirectNavigate returning
 * false skips the trip to login.microsoftonline.com, so Outlook, Teams and
 * the rest of Microsoft 365 in the same browser stay signed in.
 */
let signingOut = false;
export function signOutForInactivity(reason: 'idle' | 'manual' = 'idle') {
  if (!msalInstance || signingOut) return;
  signingOut = true;
  try {
    sessionStorage.setItem(SIGNED_OUT_KEY, reason);
  } catch {
    /* the message is a nicety */
  }
  /* The next session starts fresh, not already past the idle limit. */
  clearActivity();
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? undefined;
  void msalInstance
    .logoutRedirect({ account, onRedirectNavigate: () => false })
    .catch(() => undefined)
    .finally(() => window.location.replace(window.location.origin));
}

/**
 * The sign-in has expired and cannot be renewed silently: go through the
 * interactive sign-in once (and only once, however many requests noticed),
 * coming back to the same page.
 */
let reauthenticating = false;
export function reauthenticate() {
  if (!msalInstance || reauthenticating) return;
  reauthenticating = true;
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  void (account
    ? msalInstance.acquireTokenRedirect({ scopes: SCOPES(), account })
    : msalInstance.loginRedirect({ scopes: SCOPES() })
  ).catch(() => {
    reauthenticating = false;
  });
}

/**
 * A token for the API, renewing it if it can. Returns null, after starting
 * the sign-in, when the session can only be renewed interactively — so no
 * request goes out without one (the "A bearer token is required" error).
 */
export async function apiToken(forceRefresh = false): Promise<string | null> {
  if (DEV_BYPASS || !msalInstance) return null;
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    reauthenticate();
    return null;
  }
  try {
    return (await msalInstance.acquireTokenSilent({ scopes: SCOPES(), account, forceRefresh })).accessToken;
  } catch (err) {
    if (!forceRefresh && !(err instanceof InteractionRequiredAuthError)) {
      /* A transient failure (network, a stale cache entry): one forced renewal before giving up. */
      try {
        return (await msalInstance.acquireTokenSilent({ scopes: SCOPES(), account, forceRefresh: true })).accessToken;
      } catch {
        /* fall through to the interactive sign-in */
      }
    }
    reauthenticate();
    return null;
  }
}

/**
 * A Microsoft Graph token that may write to the signed-in person's own
 * mailbox — used to save an onboarding email as a draft in their Drafts
 * (pages/sales/onboardingEmail.ts). Delegated, so it reaches no mailbox but
 * theirs.
 *
 * Asked for only when that button is pressed, not at sign-in: most people
 * never compose one of these emails and should not be asked to consent to
 * mail access. The first time, Microsoft shows its consent prompt in a popup —
 * a popup rather than the redirect the rest of the app uses, so the email
 * being composed is still there when it closes.
 */
const MAIL_SCOPES = ['Mail.ReadWrite'];
export async function graphMailToken(): Promise<string> {
  if (DEV_BYPASS || !msalInstance) {
    throw new Error('Saving to your Drafts needs a Microsoft sign-in, which this local copy is running without.');
  }
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) throw new Error('Sign in again, then try once more.');
  try {
    return (await msalInstance.acquireTokenSilent({ scopes: MAIL_SCOPES, account })).accessToken;
  } catch (err) {
    if (!(err instanceof InteractionRequiredAuthError)) throw err;
    return (await msalInstance.acquireTokenPopup({ scopes: MAIL_SCOPES, account })).accessToken;
  }
}

interface AuthState {
  ready: boolean;
  account: AccountInfo | null;
  signIn: () => Promise<void>;
  signOut: () => void;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(DEV_BYPASS);
  const [account, setAccount] = useState<AccountInfo | null>(null);

  useEffect(() => {
    if (!msalInstance) return;
    let cancelled = false;

    msalInstance
      .initialize()
      /* Completes the redirect leg of the login. Must run before anything
         reads accounts, or a fresh sign-in looks like a failed one. */
      .then(() => msalInstance.handleRedirectPromise())
      .then((result) => {
        if (cancelled) return;
        /* A sign-in that has just completed is activity: the idle clock starts now. */
        if (result?.account) stampActivity();
        const active = result?.account ?? msalInstance.getAllAccounts()[0] ?? null;
        if (active) msalInstance.setActiveAccount(active);
        setAccount(active);
        setReady(true);
      })
      .catch((err) => {
        console.error('MSAL initialisation failed', err);
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      ready,
      account,
      async signIn() {
        if (!msalInstance) return;
        await msalInstance.loginRedirect({ scopes: apiScope ? [apiScope] : ['User.Read'] });
      },
      signOut() {
        if (!msalInstance) return;
        void msalInstance.logoutRedirect();
      },
      async getToken() {
        return apiToken();
      },
    }),
    [ready, account],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
